/* eslint-disable max-len */
import React, { useContext,useEffect } from 'react'
import GlobalContext from '../../../../components/Context/GloablContext';
import { setDashboardSearch,resetDashboardSearch } from '../../../../redux/dashboardSearchSlice';
import AdvancedSearchInputBox from '../../../../components/AdvancedSearchInput/AdvancedSearchInputBox';
import { useSelector,useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Dropdown from '../../../../components/Dropdown';

export default function DashboardAdvancedSearchbar({handleOnSubmit}) {
    const dashboardSearch = useSelector(state => state.DashboardSearchData);
    const searchApplied = useSelector((state) => state.DashboardSearchData.DashboardSearchApplied);
    const {isSideBar,setIsSideBar,isAdvancedSearchOpen,setIsAdvancedSearchOpen} = useContext(GlobalContext);
    const [searchParams,setSearchParams]=useSearchParams()
    const dispatch = useDispatch()
    const sidebarPrvState = useSelector(state => state?.systemConfigData?.sidebarPrvState)

    /**
     * The function `handleOnChangeUserDetails` updates the `dashboardSearch` state based on the
     * provided `id` and `value`, with special handling for username and email fields.
     */
    const handleOnChangeUserDetails = (id, value) => {
        let updatedValue = value;

        if (typeof updatedValue === 'string') {
            updatedValue = updatedValue;
        }
    
        const updatedState = { ...dashboardSearch, [id]: updatedValue };
    
        // Handle special cases for username and email
        if (['user_name', 'user_name_domain'].includes(id)) {
            const isUserNameEmpty = updatedState.user_name === '' && updatedState.user_name_domain === '';
            if (isUserNameEmpty) updatedState.user_name_inverse = '';
        }

        // General case for other fields
        if (updatedValue === '' && updatedState[`${id}_inverse`] === 'true') {
            updatedState[`${id}_inverse`] = '';
        }
    
        // Update the dashboardSearch state and dispatch to Redux
        setDashboardSearch(updatedState);
        dispatch(setDashboardSearch(updatedState));
    };
    
    //handleSelect checkbox functions
    const handleCheckboxChange = (e, field) => {
        const { checked } = e.target;
    
        // Create an updated dashboardSearch state
        let updatedState = { ...dashboardSearch };
    
        if (field === 'active' || field === 'inactive') {
            // Update the status array if it's a status-related checkbox
            const updatedStatus = checked 
                ? [...(dashboardSearch.status || []), field] 
                : (dashboardSearch.status || []).filter((status) => status !== field);
    
            // Uncheck status_inverse if the status array is empty
            if (updatedStatus.length === 0) {
                updatedState.status_inverse = '';
            }
    
            updatedState.status = updatedStatus;
        } else {
            // Handle other fields as boolean values
            updatedState[field] = checked ? 'true' : '';
        }
        // Dispatch the updated state
        dispatch(setDashboardSearch(updatedState));
    };
    
    // Function to handle button click
    const handleCloseClick = () => {
    // Toggle the advanced search state
        setIsAdvancedSearchOpen(!isAdvancedSearchOpen)  
        // If previous sidebar state was true and isSideBar is currently false, set it to true
        if (sidebarPrvState === true && !isSideBar) {
            setIsSideBar(true);
        }
        // Reset search state if searchApplied is false
        if (!searchApplied) {
            dispatch(resetDashboardSearch());
        }
    };

    /**
     * The function `isApplyButtonDisabled` checks if all values in the `dashboardSearch` object are
     * considered empty based on their type.
     * @returns The function `isApplyButtonDisabled` is returning a boolean value. It checks each
     * key-value pair in the `dashboardSearch` object and returns `true` if the value meets certain
     * conditions. The conditions are:
     * 1. If the key is 'match', it returns `true` (ignoring 'match').
     * 2. If the value is an array, it returns `true` if the array
     */
    const isApplyButtonDisabled = () => {
        return Object.entries(dashboardSearch).every(([key, value]) => {
            if (key === 'match') return true; // Ignore 'match'
    
            // Check if value is an array
            if (Array.isArray(value)) {
                return value.length === 0; // Return true if the array is empty
            }
    
            // Check if value is a string
            if (typeof value === 'string') {
                return value.trim() === ''; // Trim and check if it's empty
            }
    
            // For any other type (objects, numbers, etc.), return true to indicate it's 'empty'
            return true;
        });
    };

    /*** The `handleClearAll` function resets parameters and clears search-related fields.*/
    const handleClearAll = () => {
        dispatch(resetDashboardSearch());
        const updatedParams = new URLSearchParams(searchParams);
        // Only delete if the parameters exist
        if (updatedParams.has('search_value')) {
            updatedParams.delete('search_value');
        }
        if (updatedParams.has('search_mode')) {
            updatedParams.delete('search_mode');
        }
        setTimeout(() => {
            setSearchParams(Object.fromEntries(updatedParams.entries()));
        }, 300);
    };

    /* The above code is a React useEffect hook that adds an event listener for the "keydown" event on
    the window object. When a key is pressed, it checks if the key pressed is the "Escape" key. If
    the "Escape" key is pressed, it calls the `handleClearAll` function. The useEffect hook also
    includes a cleanup function that removes the event listener when the component unmounts. */
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClearAll();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);


    /* The above code snippet is a React useEffect hook that is used to parse search parameters from
    the URL and update the state based on those parameters. Here is a breakdown of what the code is
    doing: */
    useEffect(() => {
        const params = Object.fromEntries(searchParams);
        const updatedState = {}; // Always reset and update only applied fields
    
        if (params.search_value) {
            const searchDecoded = decodeURIComponent(params.search_value);
    
            // Match patterns for different fields
            const idMatch = searchDecoded.match(/(patient_id)=(ne:)?(.+?)(;|$)/);
            const deviceidMatch = searchDecoded.match(/(device_id)=(ne:)?(.+?)(;|$)/);
            const patNameMatch = searchDecoded.match(/(full_name)=(ne:)?(.+?)(;|$)/);
            const userNameMatch = searchDecoded.match(/user_name=(ne:)?(.+?)(;|$)/);
            const readingTypeMatch = searchDecoded.match(/reading_details=(ne:)?(.+?)(;|$)/);
    
            // Handle user_name field
            if (userNameMatch) {
                const isInverse = !!userNameMatch[1]; // Check if 'ne:' prefix is present
                const userNameValue = userNameMatch[2]; // Extract the user_name value after 'ne:'
                const [namePart, domainPart] = userNameValue.split('@');
                updatedState.user_name = namePart === '__NULL__' ? '-' : namePart || '';
                updatedState.user_name_domain = domainPart === '__NULL__' ? '-' : domainPart || '';
                updatedState.user_name_inverse = isInverse ? 'true' : '';
            }
    
            // Handle patient_id field
            if (idMatch) {
                const isInverse = !!idMatch[2]; // Check if 'ne:' prefix is present
                const idValue = idMatch[3]; // Extract the value after 'ne:'
                updatedState.patient_id = idValue === '__NULL__' ? '-' : idValue;
                updatedState.patient_id_inverse = isInverse ? 'true' : '';
            }
    
            // Handle device_id field
            if (deviceidMatch) {
                const isInverse = !!deviceidMatch[2]; // Check if 'ne:' prefix is present
                const idValue = deviceidMatch[3]; // Extract the value after 'ne:'
                updatedState.device_id = idValue === '__NULL__' ? '-' : idValue;
                updatedState.device_id_inverse = isInverse ? 'true' : '';
            }
    
            // Handle patient_name field
            if (patNameMatch) {
                const isInverse = !!patNameMatch[2]; // Check if `ne:` is present
                const firstNameValue = patNameMatch[3]; // Extract the actual value
                updatedState.patient_name = firstNameValue === '__NULL__' ? '-' : firstNameValue;
                updatedState.patient_name_inverse = isInverse ? 'true' : '';
            }
    
            // ** handling for `reading_details`**
            if (readingTypeMatch) {
                const isInverse = !!readingTypeMatch[1]; //Check if 'ne:' is present (Fix)
                let readingTypeValue = readingTypeMatch[2];
                // Remove leading and trailing '*' from reading_details
                readingTypeValue = readingTypeValue.replace(/^(\*)|(\*)$/g, '');
                updatedState.reading_details = readingTypeValue === '__NULL__' ? '-' : readingTypeValue;
                updatedState.reading_details_inverse = isInverse ? 'true' : ''; //Corrected inverse check
            }
            // Handle search_mode if applicable
            if (params.search_mode) {
                updatedState.match = params.search_mode === 'or' ? true : false;
            }
        }
        // Reset fields that were previously in the state but are now missing from searchParams
        Object.keys(dashboardSearch).forEach((key) => {
            if (!(key in updatedState)) {
                updatedState[key] = ''; // Clear the field if it's missing from searchParams
            }
        });
    
        dispatch(setDashboardSearch(updatedState));
    }, [isAdvancedSearchOpen]);
    
    
    return (
        <div className='absolute top-0 left-0 w-[267px] rounded-tr-[12px] rounded-br-[12px] shadow-s7 h-[100dvh] bg-[#ffffff] z-[100]'>
            <form action='' 
                onSubmit={(e) => handleOnSubmit(e)} // Existing submit handler
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault(); 
                        handleOnSubmit(e);
                    }
                }}
                className='w-full px-3 py-4'>
                <section className='flex justify-between items-center'>
                    <p className='text-sm font-semibold text-[#52555D]'>Advanced Search</p>
                    <button onClick={handleCloseClick}>
                        <img src="/images/cross_mark_gray.svg" alt="close-icon" loading='lazy' decoding='async' />
                    </button>
                </section>
                <section className='mt-4 flex items-center pb-4 border-b border-[#C5C9DA]'>
                    <p className='text-xs font-normal text-[#000000] pr-2'>Match:</p>
                    <div className='flex items-center'>
                        <div className='flex items-center'>
                            <div className="relative custom-radioBlue flex items-center">
                                <input id="match_any" type="radio" name="default-radio1" checked={dashboardSearch?.match} onChange={()=>handleOnChangeUserDetails("match",true)}/>
                                <label htmlFor="match_any" className={`text-xs font-normal ${dashboardSearch?.match ? 'text-[#000000]' : 'text-[#52555D]'} pl-6 pr-2 w-full cursor-pointer`}>
                                    Any
                                </label>
                            </div>
                            <div className='relative'>
                                <span className='any_hover'>
                                    <img src="/images/Tooltip.svg" alt="icon" loading='lazy' decoding='async' className='w-[15px] h-[15px]' />
                                </span>
                                <p className='bg-[#000000] bg-opacity-75 p-2 text-xs font-normal text-[#FFFFFF] transition-all absolute top-[25px] left-[0px] any_hover_text hidden rounded-lg w-[212px] z-[1]'>
                                    Displays results that match any of the input values. <br />
                                    Example: A OR B will return results that match either A, B, or both.
                                </p>
                            </div>
                        </div>
                        <div className='flex items-center ml-4'>
                            <div className="relative custom-radioBlue flex items-center ml-3" >
                                <input id="match_all"  type="radio" name="default-radio1" checked={!dashboardSearch?.match} onChange={()=>handleOnChangeUserDetails("match",false)}/>
                                <label htmlFor="match_all" className={`text-xs font-normal ${!dashboardSearch?.match ? 'text-[#000000]' : 'text-[#52555D]'} pl-6 pr-2 w-full cursor-pointer`}>
                                    All 
                                </label>
                            </div>
                            <div className='relative'>
                                <span className='any_hover'>
                                    <img src="/images/Tooltip.svg" alt="icon" loading='lazy' decoding='async' className='w-[15px] h-[15px]' />
                                </span>
                                <p className='bg-[#000000] bg-opacity-75 p-2 text-xs font-normal text-[#FFFFFF] transition-all absolute top-[25px] left-[0px] any_hover_text hidden rounded-lg w-[212px] z-[1]'>
                                    Displays results that match all of the input values. <br />
                                    Example: A AND B will only return results that match both A and B.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
                <div className='w-full flex items-center my-3'>
                    <div className='w-[170px] mr-2'>
                        <p className='text-xs font-normal text-[#52555D]'>Column Name & Value</p>
                    </div>
                    <div className='relative w-[65px] flex items-center'>
                        <p className='text-xs font-normal text-[#52555D]'>Inverse</p>
                        <span className='any_hover z-[99] ml-1'>
                            <img src="/images/Tooltip.svg" alt="icon" loading='lazy' decoding='async' className='w-[15px] h-[15px]' />
                        </span>
                        <p className='bg-[#000000] bg-opacity-75 p-2 text-xs font-normal text-[#FFFFFF] transition-all absolute top-[25px] left-[0px] any_hover_text hidden rounded-lg w-[212px] z-[10]'>
                            Excludes results that match the input value. <br />
                            Example: NOT A will return all results except those that match A.
                        </p>
                    </div>
                </div>
                <div className='-ml-2'>
                    <div className={`overflow-auto pl-2 h-[calc(100dvh-270px)] scrollbar_purple`}>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2'>
                            <div className='w-[170px] mr-2'>
                                <Dropdown
                                    label={"Reading Details"}
                                    id={"reading_details"}
                                    placeholder={dashboardSearch?.reading_details?.length > 0 ? '' : 'Select reading details'}
                                    data_testid={"reading_details"}
                                    value={dashboardSearch?.reading_details}
                                    options={['Missed Reading','Too High','Too Low','Too High/Too High','Too Low/Too Low','Too High/Too Low','Too Low/Too High']}
                                    handleOnChangeInput={handleOnChangeUserDetails}   
                                    searchDropdown={true}  
                                />     
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='reading_details_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'reading_details_inverse')}
                                        checked={dashboardSearch?.reading_details_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={dashboardSearch?.reading_details?.trim()===''}
                                    />
                                    <label htmlFor='reading_details_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>   
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='w-[170px] mr-2'>
                                <AdvancedSearchInputBox
                                    label={"Patient Name"}
                                    placeholder={"Enter patient name"}
                                    id={"patient_name"}
                                    data_testid={"patient_name"}
                                    htmlFor={"patient_name"}
                                    value={dashboardSearch?.patient_name}
                                    handleOnChangeInput={handleOnChangeUserDetails}
                                    maxLength={60}
                                />
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='patient_name_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'patient_name_inverse')}
                                        checked={dashboardSearch?.patient_name_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={dashboardSearch?.patient_name?.trim()===""}
                                    />
                                    <label htmlFor='patient_name_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='w-[170px] mr-2'>
                                <p className='text-xs font-normal text-[#000000] mb-1 more-lines'>Username</p>
                                <div className='w-full flex items-center'>
                                    <AdvancedSearchInputBox
                                        label={""}
                                        placeholder={"example"}
                                        id={"user_name"}
                                        data_testid={"user_name"}
                                        htmlFor={"user_name"}
                                        value={dashboardSearch?.user_name}
                                        handleOnChangeInput={handleOnChangeUserDetails}
                                        maxLength={60}
                                    />
                                    <span className='mx-1 text-xs font-normal text-[#000000]'>@</span>
                                    <AdvancedSearchInputBox
                                        label={""}
                                        placeholder={"domain"}
                                        id={"user_name_domain"}
                                        data_testid={"user_name_domain"}
                                        htmlFor={"user_name_domain"}
                                        value={dashboardSearch.user_name_domain}
                                        handleOnChangeInput={handleOnChangeUserDetails}
                                        maxLength={60}
                                    />
                                </div>
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='user_name_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'user_name_inverse')}
                                        checked={dashboardSearch?.user_name_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={dashboardSearch?.user_name_domain?.trim()===""&&dashboardSearch?.user_name?.trim()===""}
                                    />
                                    <label htmlFor='user_name_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='w-[170px] mr-2'>
                                <AdvancedSearchInputBox
                                    label={"Device ID"}
                                    placeholder={"Enter device ID"}
                                    id={"device_id"}
                                    data_testid={"device_id"}
                                    htmlFor={"device_id"}
                                    value={dashboardSearch?.device_id}
                                    handleOnChangeInput={handleOnChangeUserDetails}
                                    maxLength={30}
                                />
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='device_id_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'device_id_inverse')}
                                        checked={dashboardSearch?.device_id_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={dashboardSearch?.device_id?.trim()===""}
                                    />
                                    <label htmlFor='device_id_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='w-[170px] mr-2'>
                                <AdvancedSearchInputBox
                                    label={"ID"}
                                    placeholder={"Enter ID"}
                                    id={"patient_id"}
                                    data_testid={"patient_id"}
                                    htmlFor={"patient_id"}
                                    value={dashboardSearch.patient_id}
                                    handleOnChangeInput={handleOnChangeUserDetails}
                                    maxLength={16}
                                />
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='patient_id_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'patient_id_inverse')}
                                        checked={dashboardSearch.patient_id_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={dashboardSearch?.patient_id?.trim()===""}
                                    />
                                    <label htmlFor='patient_id_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='border-t border-[#C5C9DA]'>
                    <section className='mt-2'>
                        <p className='text-xs font-normal text-[#52555D]'>Note:</p>
                        <ol className='list-decimal pl-4 text-xs font-normal text-[#52555D]'>
                            <li>Use a dash (-) to indicate a null value.</li>
                            <li>Use an asterisk (*) as a wild-card character.</li>
                        </ol>
                    </section>
                </div>
                <div className="flex mt-[13px] justify-between">
                    <button 
                        id="Cancel"
                        type='button'
                        onClick={() => handleClearAll()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault(); // Prevent form submission
                                handleClearAll(); // Perform the clear action
                            }
                        }}
                        className="py-2.5 px-4 rounded-[16px] border border-[#972E4C] text-sm font-semibold text-[#972E4C] min-w-fit focus:bg-[#edc9d3]" 
                    >Clear All</button>
                    <button  
                        type='submit'
                        id="apply-filter"
                        disabled={isApplyButtonDisabled()}
                        className={`py-2.5 w-[106px] px-4 rounded-[16px]  disabled:bg-[#777C88] bg-[#972E4C] hover:bg-[#AB556E] border border-[#972E4C]'} text-sm font-semibold text-[#FFFFFF] hover:duration-75 reset_password_conf`}>
                        Apply
                    </button>
                </div>
            </form>
        </div>
    )
}
